import {
  AdvanceGLAccount,
  AssetGLAccount,
  DeferredGLAccount,
  ExpenseGLAccount,
  FeeRevenueGLAccount,
  type GLAccount,
  LiabilityGLAccount,
  ReceivableGLAccount,
  RevenueGLAccount,
  SuspenseGLAccount,
} from './GLAccount';

// Per-account lookup mirror of the `*GLAccount` enum buckets, for UI
// grouping. Adding a new GL account that isn't classified trips a
// compile error on `ACCOUNT_CLASS`.

export type AccountClass = 'asset' | 'liability' | 'revenue' | 'expense';

const fromOptions = <K extends string>(opts: ReadonlyArray<K>, cls: AccountClass): Record<K, AccountClass> =>
  Object.fromEntries(opts.map((k) => [k, cls])) as Record<K, AccountClass>;

export const ACCOUNT_CLASS: Record<GLAccount, AccountClass> = {
  ...fromOptions(AssetGLAccount.options, 'asset'),
  ...fromOptions(LiabilityGLAccount.options, 'liability'),
  ...fromOptions(RevenueGLAccount.options, 'revenue'),
  ...fromOptions(ExpenseGLAccount.options, 'expense'),
};

// Cross-cut aggregates for the admin ledger UI. Each group sums its
// accounts' signed balances (the caller is `deriveBalances`, which
// already applies sign convention).

export type AccountGroup = {
  key: string;
  label: string;
  accounts: ReadonlyArray<GLAccount>;
};

export const ACCOUNT_GROUPS: ReadonlyArray<AccountGroup> = [
  {
    key: 'outstandingPrincipal',
    label: 'Outstanding Principal',
    // Borrower principal exposure (UPB + billed + deferred); servicer
    // advances are excluded — they're the servicer's asset, not the
    // borrower's principal.
    accounts: ['principal', 'principalReceivable', 'deferredPrincipal'],
  },
  {
    key: 'receivables',
    label: 'Total Receivables',
    accounts: ReceivableGLAccount.options,
  },
  {
    key: 'suspense',
    label: 'Total Suspense',
    accounts: SuspenseGLAccount.options,
  },
  {
    key: 'deferred',
    label: 'Total Deferred',
    accounts: DeferredGLAccount.options,
  },
  {
    key: 'advances',
    label: 'Total Advances',
    accounts: AdvanceGLAccount.options,
  },
  {
    key: 'feeRevenue',
    label: 'Total Fee Revenue',
    accounts: FeeRevenueGLAccount.options,
  },
  {
    key: 'interestRevenue',
    label: 'Total Interest Revenue',
    accounts: ['interest', 'delinquentInterest'],
  },
  {
    key: 'cash',
    label: 'Cash',
    accounts: ['cash'],
  },
];

// Accounts whose balance is the sum of independently-tracked buckets
// keyed by an attribution field; the detail panel drills into these
// via `deriveAccountAttributionBalances`.
export const NON_FUNGIBLE_ACCOUNTS: ReadonlySet<GLAccount> = new Set<GLAccount>([
  ...ReceivableGLAccount.options,
  ...AdvanceGLAccount.options,
  ...SuspenseGLAccount.options,
  'modification',
  'advanceFunding',
]);
